import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Star, Zap, Users, Shield, BarChart3, Globe, Headphones, Music2, FileAudio, PlayCircle, Check, X } from 'lucide-react';

const Pricing = () => {
  const navigate = useNavigate();

  const handlePlanSelect = (plan: string) => {
    navigate(`/auth?plan=${plan}`);
  };

  return (
    <div className="min-h-screen bg-black text-white pt-32 pb-20 relative overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 bg-pattern animate-gradient"></div>
      <div className="gradient-orb gradient-orb-1"></div>
      <div className="gradient-orb gradient-orb-2"></div>
      <div className="gradient-orb gradient-orb-3"></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Choose Your
            <span className="block bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-purple-600 mt-2">
              Distribution Plan
            </span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Professional music distribution with no hidden fees. Keep your rights and choose the plan that fits your needs.
          </p>
        </div>

        {/* Feature Comparison */}
        <div className="mb-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex items-center space-x-4 bg-purple-500/10 p-6 rounded-lg">
              <Globe className="h-8 w-8 text-purple-400" />
              <div>
                <h3 className="font-semibold">Global Distribution</h3>
                <p className="text-gray-400">150+ streaming platforms</p>
              </div>
            </div>
            <div className="flex items-center space-x-4 bg-purple-500/10 p-6 rounded-lg">
              <Shield className="h-8 w-8 text-purple-400" />
              <div>
                <h3 className="font-semibold">Keep Your Rights</h3>
                <p className="text-gray-400">100% rights ownership</p>
              </div>
            </div>
            <div className="flex items-center space-x-4 bg-purple-500/10 p-6 rounded-lg">
              <BarChart3 className="h-8 w-8 text-purple-400" />
              <div>
                <h3 className="font-semibold">Pro Analytics</h3>
                <p className="text-gray-400">Real-time tracking</p>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Basic Plan */}
          <div className="relative bg-gray-900/50 rounded-2xl p-8 border border-purple-500/20 hover:border-purple-500/40 transition-all transform hover:scale-105">
            <div className="absolute -top-4 -right-4">
              <Zap className="h-8 w-8 text-purple-400" />
            </div>
            <h3 className="text-2xl font-bold mb-2">Starter</h3>
            <div className="mb-6">
              <span className="text-4xl font-bold">$9.99</span>
              <span className="text-gray-400">/month</span>
            </div>
            <div className="space-y-6 mb-8">
              <FeatureGroup title="Distribution" features={[
                { text: "1 Artist Profile", available: true },
                { text: "Keep 80% Revenue", available: true },
                { text: "150+ Platforms", available: true },
                { text: "Standard Release Speed", available: true },
                { text: "Priority Distribution", available: false },
                { text: "Custom Release Schedule", available: false }
              ]} />
              <FeatureGroup title="Analytics" features={[
                { text: "Basic Analytics", available: true },
                { text: "Monthly Reports", available: true },
                { text: "Revenue Dashboard", available: true },
                { text: "Real-time Analytics", available: false },
                { text: "Audience Insights", available: false },
                { text: "Custom Reports", available: false }
              ]} />
              <FeatureGroup title="Support" features={[
                { text: "Email Support", available: true },
                { text: "Knowledge Base", available: true },
                { text: "Priority Support", available: false },
                { text: "1-on-1 Guidance", available: false },
                { text: "Marketing Support", available: false }
              ]} />
            </div>
            <Link 
              to="/auth?plan=basic"
              className="w-full bg-gradient-to-r from-purple-500 to-purple-700 py-3 rounded-lg hover:from-purple-600 hover:to-purple-800 transition-colors block text-center"
            >
              Get Started
            </Link>
          </div>

          {/* Pro Plan */}
          <div className="relative bg-gradient-to-b from-purple-900/30 to-gray-900/50 rounded-2xl p-8 border-2 border-purple-500 transform hover:scale-105 transition-all">
            <div className="absolute -top-4 -right-4">
              <Star className="h-8 w-8 text-purple-400" />
            </div>
            <div className="inline-block bg-gradient-to-r from-purple-500 to-purple-600 text-white text-sm px-3 py-1 rounded-full mb-4">
              Most Popular
            </div>
            <h3 className="text-2xl font-bold mb-2">Pro</h3>
            <div className="mb-6">
              <span className="text-4xl font-bold">$19.99</span>
              <span className="text-gray-400">/month</span>
            </div>
            <div className="space-y-6 mb-8">
              <FeatureGroup title="Distribution" features={[
                { text: "2 Artist Profiles", available: true },
                { text: "Keep 100% Revenue", available: true },
                { text: "150+ Platforms", available: true },
                { text: "Priority Distribution", available: true },
                { text: "Express Release (24h)", available: true },
                { text: "Custom Release Schedule", available: false }
              ]} highlight />
              <FeatureGroup title="Analytics" features={[
                { text: "Advanced Analytics", available: true },
                { text: "Real-time Reports", available: true },
                { text: "Revenue Forecasting", available: true },
                { text: "Audience Insights", available: true },
                { text: "Custom Reports", available: false },
                { text: "API Access", available: false }
              ]} highlight />
              <FeatureGroup title="Support" features={[
                { text: "Priority Support", available: true },
                { text: "1-on-1 Guidance", available: true },
                { text: "Release Planning", available: true },
                { text: "Marketing Support", available: false },
                { text: "Dedicated Manager", available: false }
              ]} highlight />
            </div>
            <Link 
              to="/auth?plan=pro"
              className="w-full bg-gradient-to-r from-purple-400 to-purple-600 py-3 rounded-lg hover:from-purple-500 hover:to-purple-700 transition-colors shadow-lg shadow-purple-500/25 block text-center"
            >
              Get Started
            </Link>
          </div>

          {/* Label Plan */}
          <div className="relative bg-gray-900/50 rounded-2xl p-8 border border-purple-500/20 hover:border-purple-500/40 transition-all transform hover:scale-105">
            <div className="absolute -top-4 -right-4">
              <Users className="h-8 w-8 text-purple-400" />
            </div>
            <h3 className="text-2xl font-bold mb-2">Label</h3>
            <div className="mb-6">
              <span className="text-4xl font-bold">$49.99</span>
              <span className="text-gray-400">/month</span>
            </div>
            <div className="space-y-6 mb-8">
              <FeatureGroup title="Distribution" features={[
                { text: "10 Artist Profiles", available: true },
                { text: "Keep 100% Revenue", available: true },
                { text: "150+ Platforms", available: true },
                { text: "Priority Distribution", available: true },
                { text: "Express Release (24h)", available: true },
                { text: "Custom Release Schedule", available: true }
              ]} />
              <FeatureGroup title="Analytics" features={[
                { text: "Label Dashboard", available: true },
                { text: "Artist Performance", available: true },
                { text: "Revenue Splitting", available: true },
                { text: "Custom Reports", available: true },
                { text: "API Access", available: true },
                { text: "Data Export", available: true }
              ]} />
              <FeatureGroup title="Support" features={[
                { text: "Dedicated Manager", available: true },
                { text: "24/7 Support", available: true },
                { text: "Strategy Sessions", available: true },
                { text: "Marketing Support", available: true },
                { text: "Release Planning", available: true }
              ]} />
            </div>
            <Link 
              to="/auth?plan=label"
              className="w-full bg-gradient-to-r from-purple-500 to-purple-700 py-3 rounded-lg hover:from-purple-600 hover:to-purple-800 transition-colors block text-center"
            >
              Get Started
            </Link>
          </div>
        </div>

        {/* Process Section */}
        <div className="mt-20">
          <h2 className="text-3xl font-bold text-center mb-12">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <ProcessStep
              icon={<FileAudio className="h-8 w-8" />}
              title="Upload Music"
              description="Upload your tracks in WAV format"
            />
            <ProcessStep
              icon={<Music2 className="h-8 w-8" />}
              title="Add Details"
              description="Complete metadata and artwork"
            />
            <ProcessStep
              icon={<Globe className="h-8 w-8" />}
              title="Choose Stores"
              description="Select your distribution platforms"
            />
            <ProcessStep
              icon={<PlayCircle className="h-8 w-8" />}
              title="Release"
              description="Your music goes live worldwide"
            />
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-20">
          <h2 className="text-3xl font-bold text-center mb-10">Frequently Asked Questions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <FaqItem 
              question="How long does distribution take?"
              answer="Most releases are live on major platforms within 24-48 hours. Some platforms may take up to 5 business days."
            />
            <FaqItem 
              question="Do I keep all my rights?"
              answer="Yes, you maintain 100% ownership of your music. We're just here to help you distribute it."
            />
            <FaqItem 
              question="What formats do you accept?"
              answer="We accept WAV files (16-bit or 24-bit, 44.1kHz). Your music must meet professional quality standards."
            />
            <FaqItem 
              question="Can I change my plan later?"
              answer="Yes, you can upgrade or downgrade your plan at any time. Changes take effect at the start of your next billing cycle."
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const FeatureGroup = ({ title, features, highlight = false }: { title: string, features: { text: string, available: boolean }[], highlight?: boolean }) => {
  return (
    <div>
      <h4 className="font-semibold mb-3 text-purple-400">{title}</h4>
      <ul className="space-y-2">
        {features.map((feature, index) => (
          <li key={index} className="flex items-center space-x-3">
            {feature.available ? (
              <Check className={`h-5 w-5 ${highlight ? 'text-purple-400' : 'text-purple-500'}`} />
            ) : (
              <X className="h-5 w-5 text-gray-500" />
            )}
            <span className={feature.available ? (highlight ? 'text-white' : 'text-gray-300') : 'text-gray-500'}>
              {feature.text}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ProcessStep = ({ icon, title, description }: { icon: React.ReactNode, title: string, description: string }) =>  {
  return (
    <div className="flex flex-col items-center text-center group">
      <div className="bg-purple-500/10 p-4 rounded-lg mb-4 text-purple-400 group-hover:bg-purple-500/20 transition-all">
        {icon}
      </div>
      <h3 className="text-lg font-semibold mb-2">{title}</h3>
      <p className="text-gray-400">{description}</p>
    </div>
  );
};

const FaqItem = ({ question, answer }: { question: string, answer: string }) => {
  return (
    <div className="bg-gray-900/50 rounded-lg p-6 border border-purple-500/20 hover:border-purple-500/40 transition-all">
      <h3 className="text-lg font-semibold mb-2">{question}</h3>
      <p className="text-gray-400">{answer}</p>
    </div>
  );
};

export default Pricing;